import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

/**
 * 「제어」 카드 「상태」 줄의 판정 — `ModuleSnapshot` 의 `e_stop` · `hold` · `interlock` 세 칸을 **그대로** 읽는다
 * (eric 2026-10-22 「깔끔하게 추가해줘」 · 팀장 제안 그림 「상태 안전정지 · 뒷문 열림(닫는 중)」).
 *
 * | 갈래 | 조건 | 화면 |
 * | --- | --- | --- |
 * | 걸린 것 | E-STOP 눌림 · 보류(`hold.kind` ≠ `none`) · 인터락 열림 · 문 열림/구동 이상 | 칩 여럿(위험은 붉은 톤) |
 * | 정상 | 셋 다 알고(`e_stop === false` · `hold.kind === 'none'` · `interlock.state === 'closed'`) 걸린 것이 없다 | 「정상」 |
 * | 모름 | 걸린 것이 없는데 셋 중 하나라도 미발행(`null`) | `—` — 🔴 「정상」으로 접지 않는다 |
 *
 * 🔴 값을 지어내지 않는다 — 문구는 사전(`enum.holdKind` · `enum.interlock` · `enum.doorState` · `enum.doorDrive`)이고, 사전에 없는 값은 원값이다.
 * ⚠ 문 이름은 모니터링 6축 라인의 「앞문 · 뒷문」과 한 벌이다(`monitoring.robotLine.door.*`).
 */
export interface StatusPart {
  key: string
  label: string
  /** 위험 — E-STOP · 안전정지 · 보호정지 · 인터락/문 열림 · 문 구동 이상 */
  danger: boolean
  /** 원값(툴팁) — 보류의 출처(`hold.source`) 같은 것 */
  detail?: string | undefined
}

export type ModuleControlStatus =
  { kind: 'normal' } | { kind: 'unknown' } | { kind: 'issues'; parts: StatusPart[] }

type StatusInput = Pick<ModuleSnapshot, 'e_stop' | 'hold' | 'interlock'>

/** 사람을 멈춰 세우는 보류 — 붉은 톤. 일시정지 · 소진은 위험이 아니다(사람이 할 일이 보충 · 재개다) */
const DANGER_HOLDS: ReadonlySet<string> = new Set([
  'emergency_stop',
  'safety_stop',
  'protective_stop',
])

function doorParts(interlock: StatusInput['interlock']): StatusPart[] {
  const doors = interlock?.doors ?? []
  return doors.flatMap((door): StatusPart[] => {
    const drive = door.drive ?? undefined
    const open = door.state === 'open'
    const fault = drive === 'fault'
    if (!open && !fault) return []
    const face = t(`monitoring.robotLine.door.${door.face}`)
    const state = enumLabel('enum.doorState', door.state)
    /* 구동부가 문 상태와 다른 말을 하면 괄호로 같이 적는다(「뒷문 열림(닫는 중)」) — 같으면 한 번만 */
    const driveText =
      drive === undefined || drive === door.state ? '' : `(${enumLabel('enum.doorDrive', drive)})`
    return [{ key: `door-${door.face}`, label: `${face} ${state}${driveText}`, danger: true }]
  })
}

export function moduleControlStatus(module: StatusInput): ModuleControlStatus {
  const parts: StatusPart[] = []
  if (module.e_stop === true)
    parts.push({ key: 'estop', label: t('module.eStopEngaged'), danger: true })

  const holdKind = module.hold?.kind ?? null
  if (holdKind !== null && holdKind !== 'none')
    parts.push({
      key: 'hold',
      label: enumLabel('enum.holdKind', holdKind),
      danger: DANGER_HOLDS.has(holdKind),
      detail: module.hold?.source ?? undefined,
    })

  const interlockState = module.interlock?.state ?? null
  const doors = doorParts(module.interlock)
  if (doors.length > 0) parts.push(...doors)
  else if (interlockState !== null && interlockState !== 'closed')
    parts.push({
      key: 'interlock',
      label: enumLabel('enum.interlock', interlockState),
      danger: true,
    })

  if (parts.length > 0) return { kind: 'issues', parts }
  const known = module.e_stop === false && holdKind === 'none' && interlockState === 'closed'
  return known ? { kind: 'normal' } : { kind: 'unknown' }
}
